/**
 * @module support/pdl
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
// tslint:disable:no-string-literal no-global-variables
// tslint:disable:no-any
// tslint:disable

import { Injectable } from '@angular/core';
import { NgRadio } from '@experience/reb-pdl/components/ng-radio.component';
import {
  PdlDataInterface,
  PdlEventDataObject,
  TrackDataObject,
} from '../interfaces/pdl-data.interface';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';

@Injectable({
  providedIn : 'root'
})
export class PdlService {
  private eventConfig: PdlDataInterface;
  private dataTrackConfig: any;
  private customHandlers: any;
  private dataTrackConfigured: boolean;
  // private dataTrackListener: any;
  private componentPersistence: any;

  constructor(
    public eventBus: NgRadio,
    private objectService: ObjectService
  ) {
    this.componentPersistence = {};
    this.eventBus.on('pdl:send-data-event').subscribe((pdlData: PdlEventDataObject) => {
      this.dataTrackConfigured = true;
      const curwindow = window;
      const event = new CustomEvent('TargetPageLoad', {
        detail: {
          viewName: curwindow['digitalData'].page.pageInfo.pageName,
        },
      });
      window.dispatchEvent(event);
      curwindow['digitalData'].events.push(pdlData.eventObject);
      // curwindow["digitalData"].triggerEvent(pdlData.eventAnalytics);
      if (curwindow['triggerEvent']) {
        curwindow['triggerEvent']();
      }
      console.log(
        'pdl:component:sendDataEvent:digitalData: pdl event pushed to tealium',
        curwindow['digitalData']
      );
    });

    this.eventBus.on('pdl: disable-tracking-event').subscribe(() => {
      this.dataTrackConfigured = false;
    });

    this.eventBus.on('pdl: config-data-track-event').subscribe((data: any) => {
      if (data && typeof data === 'object') {
        this.dataTrackConfig = data;
        if (!this.dataTrackConfigured) {
          this.dataTrackConfigured = true;
        }
      } else {
        console.error('[ERROR]: Config needs to be an object');
      }
    });

    this.eventBus.on('pdl: track-data-event').subscribe((dataInfo: TrackDataObject) => {
      if (this.dataTrackConfigured) this.trackData(dataInfo);
    });

    this.eventBus.on('pdl: config-handlers-event').subscribe((data: any) => {
      console.info('PDL:Config-handler-event :', data);
      if (data && typeof data === 'object') {
        this.customHandlers = data;
      } else {
        console.error('[ERROR]: Handler needs to be an object');
      }
    });
  }

  /**
   * Initializes the data for device validation
   * @returns void
   */
  public ngOnInit(): void {}

  public getEventBus(): NgRadio {
    return this.eventBus;
  }

  public getPDLData(global: boolean = false): PdlDataInterface {
    return this.eventConfig && this.eventConfig[global ? 'global' : 'page'];
  }

  public setPDLData(data: any, global: boolean = false): void {
    if (data && typeof data === 'object') {
      if (!this.eventConfig) {
        this.eventConfig = {};
      }
      this.eventConfig[global ? 'global' : 'page'] = data;
      this.eventBus.cast('pdl:enablePDL:EventListeners');
    } else {
      console.error('[ERROR]: PdlData needs to be an object');
    }
  }

  public triggerCustomEvent(cmpId: string, ctaId: string): void {
    console.log('PDL:PDL_SERVICE: Triggering custom event...');
    let pdlData: PdlEventDataObject = this.setCustomEventDataObject(cmpId, ctaId);
    if (this.enabled(pdlData)) {
      console.log('pdl:service: pdl custom event enabled:', pdlData);
      this.eventBus.cast('pdl:capture-data-event', pdlData);
    }
  }

  public enabled(pdlEvent: PdlEventDataObject): boolean {
    this.setEventObj(pdlEvent);
    return pdlEvent.eventObject ? true : false;
  }

  private setEventObj(pdlEvent: PdlEventDataObject, globalConfig: boolean = false): void {
    const pdlPageData = this.getPDLData(globalConfig);
    let componentId = this.extractConfigCmpId(pdlEvent);
    componentId = this.configExists(pdlEvent, componentId, pdlPageData) ? componentId : '*';
    if (this.configExists(pdlEvent, componentId, pdlPageData)) {
      pdlEvent.eventObject =
        pdlPageData[componentId][pdlEvent.ctaId][pdlEvent.action].eventObjectTemplate;
      pdlEvent.fieldMap = pdlPageData[componentId][pdlEvent.ctaId][pdlEvent.action].map;
      pdlEvent.eventAnalytics =
        pdlPageData[componentId][pdlEvent.ctaId][pdlEvent.action].eventAnalytics;
      pdlEvent.handlers = pdlPageData[componentId][pdlEvent.ctaId][pdlEvent.action].handlers;
    } else if (!globalConfig) {
      this.setEventObj(pdlEvent, true);
    }
  }

  private configExists(pdlEvent, componentId, config) {
    return (
      config &&
      config[componentId] &&
      config[componentId][pdlEvent.ctaId] &&
      config[componentId][pdlEvent.ctaId][pdlEvent.action]
    );
  }

  private extractConfigCmpId(pdlEvent: PdlEventDataObject): string {
    if (pdlEvent && pdlEvent.cmpId) {
      return pdlEvent.cmpId.slice(
        pdlEvent.cmpId.lastIndexOf('#') + 1 || 0,
        pdlEvent.repeatedComponent
          ? pdlEvent.cmpId.lastIndexOf('_') || pdlEvent.cmpId.length
          : pdlEvent.cmpId.length
      );
    }
  }

  private setCustomEventDataObject(cmpId: string, ctaId: string): PdlEventDataObject {
    return {
      action: 'custom',
      cmpId: cmpId,
      ctaId: ctaId,
      repeatedComponent: false,
    };
  }

  public transform(config: any, srcObj: any, destObj: any): void {
    if (config.map && Array.isArray(config.map)) {
      config.map.forEach((configObj: any) => {
        this.map(configObj, srcObj, destObj);
      });
    } else {
      // console.error('[ERROR]: Config.map is incorrect or not defined', config,
      // config.map, Array.isArray(config.map));
    }
    if (config.handlers && Array.isArray(config.handlers) && config.handlers.length > 0) {
      config.handlers.forEach((handlerName: string) => {
        destObj = this.runHandler(handlerName, srcObj, destObj);
      });
    } else {
      // console.error('[ERROR]: Config.handler is incorrect or not defined', config, config.handlers);
    }
  }

  public map(config: any, srcObj: any, destObj: any): void {
    const setVal = this.objectService.has(srcObj, config.srcPath)
      ? this.objectService.get(srcObj, config.srcPath)
      : config.default
        ? config.default
        : this.objectService.has(destObj, config.destPath)
          ? this.objectService.get(destObj, config.destPath)
          : '';
    this.objectService.set(destObj, config.destPath, setVal);
  }

  public runHandler(
    handlerName: string,
    src: object,
    dest: object,
    context?: object,
    config?: object
  ): object {
    if (this.customHandlers && this.customHandlers[handlerName]) {
      let newDest: object = dest;
      try {
        newDest = this.customHandlers[handlerName](src, dest);
      } catch (e) {
        console.error('[ERROR]: Handler "', handlerName, '" failed with error: ', e);
      }
      return newDest;
    } else {
      console.error('[ERROR]: No handler defined for "', handlerName, '"');
    }
  }

  private trackData(dataInfo: TrackDataObject) {
    let pageConfig = (this.dataTrackConfig && this.dataTrackConfig.global) || {};
    pageConfig =
      this.dataTrackConfig && this.dataTrackConfig[dataInfo.pageIdentifier]
        ? this.dataTrackConfig[dataInfo.pageIdentifier]
        : pageConfig;
    if (pageConfig[dataInfo.ctaId]) {
      const curwindow = window;
      const digitalData = curwindow['digitalData'];
      if (pageConfig[dataInfo.ctaId].common) {
        if (
          this.dataTrackConfig._lib &&
          this.dataTrackConfig._lib[pageConfig[dataInfo.ctaId].common]
        ) {
          this.transform(
            this.dataTrackConfig._lib[pageConfig[dataInfo.ctaId].common],
            dataInfo.data,
            digitalData
          );
        } else {
          console.error(
            this.dataTrackConfig._lib,
            pageConfig[dataInfo.ctaId].common,
            this.dataTrackConfig._lib[pageConfig[dataInfo.ctaId].common]
          );
        }
      }
      if (pageConfig[dataInfo.ctaId].map || pageConfig[dataInfo.ctaId].handlers) {
        this.transform(pageConfig[dataInfo.ctaId], dataInfo.data, digitalData);
      }
    }
  }

  public populateEventObject(message: PdlEventDataObject, context: any): void {
    message.eventObject = this.recursiveStringInterpolation(message.eventObject, context);
    if (message.fieldMap && Array.isArray(message.fieldMap)) {
      message.fieldMap.forEach((config) => {
        const srcObj = context[config.src];
        const destObj = message.eventObject;
        const setVal = config.srcPath
          ? this.objectService.has(srcObj, config.srcPath)
            ? this.objectService.get(srcObj, config.srcPath)
            : config.default
              ? config.default
              : this.objectService.has(destObj, config.destPath)
                ? this.objectService.get(destObj, config.destPath)
                : ''
          : config.src
            ? context[config.src]
              ? context[config.src]
              : ''
            : '';
        this.objectService.set(message.eventObject, config.destPath, setVal);
      });
    }

    if (message.handlers && Array.isArray(message.handlers)) {
      message.handlers.forEach((handler) => {
        this.runHandler(handler, context, message.eventObject);
      });
    }
    this.eventBus.cast('pdl:send-data-event', message);
  }

  // Construction in progress *
  private recursiveStringInterpolation(curr: Object | string, context: any): any {
    if (curr) {
      if (typeof curr === 'string') {
        // we are at a leaf
        const interpolationArr = curr.match(/\{\{.*?\}\}/gi);
        if (interpolationArr && interpolationArr.length > 0) {
          let interpollatedStr: string = curr;
          interpolationArr.forEach((interpolation) => {
            interpollatedStr = interpollatedStr.replace(
              interpolation,
              this.stringReplace(interpolation, context)
            );
          });
          return interpollatedStr;
        } else {
          return curr;
        }
      } else if (curr instanceof Object) {
        let retVal = {};
        const obj: Object = curr;
        for (let key in obj) {
          let newString = this.recursiveStringInterpolation(obj[key], context);
          retVal[key] = newString;
        }
        return retVal;
      }
    }
  }

  private stringReplace(template: String, context: any): string {
    let retval = '';
    let tmp;
    const pathArr = template.replace('{{', '').replace('}}', '').split('.');
    if (pathArr.length > 1) {
      const src = pathArr.shift();
      const srcPath = pathArr.join('.');
      tmp = this.objectService.get(context[src], srcPath) || '';
    } else {
      tmp = context[pathArr[0]] || '';
    }
    if (Array.isArray(tmp)) {
      retval = tmp.join(',');
    } else if (typeof tmp === 'object') {
      Object.getOwnPropertyNames(tmp).forEach((key) => {
        retval += key + ':' + tmp[key] + '|';
      });
      if (retval) {
        retval = retval.slice(0, -1);
      }
    } else {
      retval = tmp;
    }
    return retval;
  }

  public persistData(key: string, data: any, ttl: number = 2000): void {
    console.log('pdl:service: persisting key ', key);
    this.componentPersistence[key] = data;
    setTimeout(() => {
      console.log('pdl:service: timeout achieved, deleting key ', key);
      delete this.componentPersistence[key];
    }, ttl);
  }

  public getPersistedData(key: string): any {
    if (key && this.componentPersistence[key]) {
      return this.componentPersistence[key];
    }
  }
}
